import React, { useEffect, useRef, useState } from 'react'
import SplitText from './SplitText'
import Button from './Button'
import InputSelect from './InputSelect'
import CursorZoom from './CursorZoom'
import Picture from './Picture'

const DesignCustomization = ({
	introImage,
	items,
	isActivated,
	// This just to ensure that the 'Fabrik' product category color images have the
	// black circle overlay (like glazings) and can't be zoomed in. Weird client request at the end.
	forceOverlayOnColorTypeImages = false
}) => {

	const [isMobile, setIsMobile] = useState(true)

	useEffect(() => {
		setIsMobile(localStorage.getItem('is-touch') === 'true')
	}, [])

	const containerEl = useRef(null)
	const itemsAccordeonsEls = useRef([])
	const [currentIndex, updateCurrentIndex] = useState(null)

	/**
	* Sort select categories
	*/
	const types = ['Colors', 'Shapes', 'Glazes - Standard', 'Glazes - Effects', 'Textures']
	// Create a 2d array containing items by types
	const sortedItems = types.map(type => {
		return items.filter(item => item.type === type);
	})

	/**
	* Accordion logic
	*/
	// Set height depending on content height
	const setAccordeonSizes = () => {
		itemsAccordeonsEls.current.forEach(itemEl => {
			const closedHeight = itemEl.querySelector('.DesignCustomization__TypeHeader').getBoundingClientRect().height;
			const contentEl = itemEl.querySelector('.DesignCustomization__TypeList');
			const contentElHeight = contentEl.getBoundingClientRect().height;
			const openHeightInPx = closedHeight + contentElHeight;
			const openHeightInVw = 100 * (openHeightInPx / window.innerWidth);
			itemEl.style.height = closedHeight + 'px';
			itemEl.setAttribute('data-closed-height', closedHeight);
			itemEl.setAttribute('data-open-height', openHeightInVw);
		})
	}

	const initFirstAccordeon = () => {
		// If first type is empty
		let startIndex = null;
		sortedItems.forEach((type, itemIndex) => {
			const reversedIndex = sortedItems.length - (itemIndex + 1);
			if (sortedItems[reversedIndex].length > 0) {
				startIndex = reversedIndex
			}
		})
		updateCurrentIndex(startIndex);
		const newItem = itemsAccordeonsEls.current[startIndex];
		newItem.classList.add('current');
		newItem.style = `height: ${newItem.getAttribute('data-open-height')}vw;`;
	}

	const handleOnAccordeonKeyDown = e => {
		if (e.key === 'Enter') {
			onAccordeonItemClick();
		}
	}

	const onAccordeonItemClick = e => {
		const newIndex = e.target.closest('[data-accordeon-design-index]').getAttribute('data-accordeon-design-index');

		if (currentIndex === newIndex) return;

		const previousItem = itemsAccordeonsEls.current[currentIndex];
		if (previousItem) {
			previousItem.classList.remove('current');
			previousItem.style.height = previousItem.getAttribute('data-closed-height') + 'px';
		};

		const newItem = itemsAccordeonsEls.current[newIndex];
		newItem.classList.add('current');
		newItem.style = `height: ${newItem.getAttribute('data-open-height')}vw;`;

		updateCurrentIndex(parseInt(newIndex));

		updateCurrentImageAfterTypeChange(newIndex)
	}

	/**
	* Click or select items
	*/
	const labelsEl = useRef(null)

	const initFirstItem = () => {
		containerEl.current.querySelector('.DesignCustomization__Image:first-child').classList.add('current');
		labelsEl.current.querySelector('.DesignCustomization__LabelTexts:first-child').classList.add('current');
		// Only in the case where the first accordeon have checkboxes
		containerEl.current.querySelector('.DesignCustomization__TypeItemCheckbox:first-child').classList.add('current');
	}

	const handleCheckboxClick = e => {
		setIsZoomedIn(false)
		resetZoom()

		// Get button el
		const mainTargetEl = e.target.closest('[data-custom-type-index]');

		// Get images infos
		const typeIndex = mainTargetEl.getAttribute('data-custom-type-index');
		const itemIndex = mainTargetEl.getAttribute('data-custom-item-index');

		// Get image and label
		const newImageEl = containerEl.current.querySelector(`[data-custom-images-2d-index='${typeIndex}-${itemIndex}']`)
		const newLabelEl = labelsEl.current.querySelector(`[data-custom-label-2d-index='${typeIndex}-${itemIndex}']`)

		// Update image
		containerEl.current.querySelector('.DesignCustomization__Image.current').classList.remove('current');
		newImageEl.classList.add('current');

		// Update label
		labelsEl.current.querySelector('.DesignCustomization__LabelTexts.current').classList.remove('current');
		newLabelEl.classList.add('current')

		// Update button el
		containerEl.current.querySelector('.DesignCustomization__TypeItemCheckbox.current').classList.remove('current');
		mainTargetEl.classList.add('current');
	}

	const handleOnCheckboxKeyDown = e => {
		if (e.key === 'Enter') {
			handleCheckboxClick();
		}
	}

	const handleSelectChange = (e, type) => {
		setIsZoomedIn(false)
		resetZoom()
		// Get image
		const newImageEl = containerEl.current.querySelector(`img[src='${e.image1.fluid.src}']`)

		// Get label
		const newLabelEl = labelsEl.current.querySelector(`[data-label='${e.label}']`)

		// Update image
		containerEl.current.querySelector('.DesignCustomization__Image.current').classList.remove('current');
		newImageEl.classList.add('current');

		// Update label
		labelsEl.current.querySelector('.DesignCustomization__LabelTexts.current').classList.remove('current');
		newLabelEl.classList.add('current')

	}

	const updateCurrentImageAfterTypeChange = (newIndex) => {
		setIsZoomedIn(false)
		resetZoom()

		// Get previous image and hide it
		const prevImage = containerEl.current.querySelector('.DesignCustomization__Image.current');
		if (prevImage) {
			prevImage.classList.remove('current');
		}

		// Get new image and show it
		const newImageEl = containerEl.current.querySelector(`[data-custom-images-2d-index='${newIndex}-0']`)
		if (newImageEl) {
			newImageEl.classList.add('current');
		}

		// Get previous label and hide it
		const prevLabelEl = labelsEl.current.querySelector('.DesignCustomization__LabelTexts.current');
		if (prevLabelEl) {
			prevLabelEl.classList.remove('current');
		}

		// Get new label and show it
		const newlabelEl = containerEl.current.querySelector(`[data-custom-label-2d-index='${newIndex}-0']`)
		if (newlabelEl) {
			newlabelEl.classList.add('current');
		}

		// Update checkboxes if one was checked before
		const prevCheckbox = containerEl.current.querySelector('.DesignCustomization__TypeItemCheckbox.current');
		if (prevCheckbox) {
			prevCheckbox.classList.remove('current');
		}

		// Update checkboxes in new accordeon item if it contains checkboxes
		const nextCheckbox = itemsAccordeonsEls.current[newIndex].querySelector('.DesignCustomization__TypeItemCheckbox')
		if (nextCheckbox) {
			nextCheckbox.classList.add('current')
		}
	}

	/**
	*	Open and close the Design Customization
	*/
	const [isOpen, setIsOpen] = useState(false)
	const handleIntroButtonClick = () => {
		setIsOpen(true)
		requestAnimationFrame(() => {
			setAccordeonSizes()
			initFirstAccordeon()
			initFirstItem()
		});
	}

	const handleCloseButtonClick = () => {
		setIsOpen(false)
		setIsZoomedIn(false)
		setTimeout(() => {
			resetZoom()
		}, 1000);
		const previousItem = itemsAccordeonsEls.current[currentIndex];
		if (previousItem) {
			previousItem.classList.remove('current');
			previousItem.style.height = previousItem.getAttribute('data-closed-height') + 'px';
		}
		const prevImage = containerEl.current.querySelector('.DesignCustomization__Image.current');
		if (prevImage) {
			prevImage.classList.remove('current');
		}
		const prevLabelEl = labelsEl.current.querySelector('.DesignCustomization__LabelTexts.current');
		if (prevLabelEl) {
			prevLabelEl.classList.remove('current');
		}
		const prevCheckbox = containerEl.current.querySelector('.DesignCustomization__TypeItemCheckbox.current');
		if (prevCheckbox) {
			prevCheckbox.classList.remove('current');
		}
	}

	/**
	*	Zoom logic
	*/
	const zoomWrapperEl = useRef(null)
	const zoomTranslateEl = useRef(null)
	const imageWrapperEl = useRef(null)
	const [isZoomedIn, setIsZoomedIn] = useState(false)

	let ww = 0
	let wh = 0
	if (typeof window !== `undefined`) {
		ww = window.innerWidth
		wh = window.innerHeight
	}
	// Change those variables if css change
	const zoomWrapperElLeft = ww * 0.25
	const zoomWrapperElWidth = ww * 0.75
	const zoomWrapperElHeight = wh
	const zoomWrapperElWidthHalf = zoomWrapperElWidth * 0.5
	const zoomWrapperElHeightHalf = zoomWrapperElHeight * 0.5
	const zoomScale = 1.8
	const translateRatio = 1 / zoomScale

	const handleZoomWrapperClick = e => {
		if (currentIndex === 2 || currentIndex === 3 || (forceOverlayOnColorTypeImages && currentIndex === 0)) return
		setIsZoomedIn(!isZoomedIn)
		if (isZoomedIn === true) {
			resetZoom()
		} else {
			imageWrapperEl.current.style.transform = `scale(${zoomScale})`
			updateZoomTranslation(e)
		}
	}

	const resetZoom = () => {
		zoomTranslateEl.current.style.transform = `none`
		imageWrapperEl.current.style.transform = `none`
	}

	const handleZoomWrapperMouseMove = e => {
		if (!isZoomedIn) return
		updateZoomTranslation(e)
	}

	const updateZoomTranslation = e => {
		const mouseX = e.clientX - zoomWrapperElLeft
		const mouseY = e.clientY

		const distFromCenterX = mouseX - zoomWrapperElWidthHalf
		const distFromCenterY = mouseY - zoomWrapperElHeightHalf

		zoomTranslateEl.current.style.transform = `translate3d(${-distFromCenterX * translateRatio}px, ${-distFromCenterY * translateRatio}px, 0)`
	}

	return (
		<div
			className="DesignCustomization"
			ref={containerEl}
			data-is-on-screen
		>

			<div className="DesignCustomization__Intro">

				<div className="DesignCustomization__IntroLeft">
					<h3 className="DesignCustomization__Title">
						<SplitText splitBy="chars">
							Design Customization
						</SplitText>
					</h3>

					<p className="DesignCustomzation__IntroLeftMobile">
						To get the best experience, please see the design customization on desktop.
					</p>

					{isActivated !== false &&
						<Button
							onClick={handleIntroButtonClick}
							modifier="normal"
							className="DesignCustomzation__IntroLeftButton"
						>
							Get Started
						</Button>
					}

					{isActivated === false &&
						<div className="DesignCustomization__IntroLeftComingSoon">
							Design customization is coming soon.
						</div>
					}
				</div>

				<div className="DesignCustomization__IntroRight">
					<div className="DesignCustomization__IntroImageWrapper">
						{introImage.fluid &&
							<Picture
								className="DesignCustomization__IntroImage"
								src={introImage.fluid.src}
								srcSet={introImage.fluid.srcSet}
								alt={introImage.fluid.alt}
								sizes="70vw"
							/>
						}
					</div>
				</div>

			</div>

			{isMobile === false &&

				<div className={`DesignCustomization__Popup ${isOpen ? 'DesignCustomization__Popup--isOpen' : ''}`}>

					<CursorZoom isZoomedIn={isZoomedIn} isVisible={((currentIndex === 0 && !forceOverlayOnColorTypeImages) || currentIndex === 1 || currentIndex === 4)} />

					<div
						className="DesignCustomization__PopupClose"
						onClick={handleCloseButtonClick}
						data-cursor-hide
					>
						<div className="DesignCustomization__PopupCloseLines">
							<div className="DesignCustomization__PopupCloseLine DesignCustomization__PopupCloseLine--Left"></div>
							<div className="DesignCustomization__PopupCloseLine DesignCustomization__PopupCloseLine--Right"></div>
						</div>
					</div>

					<div
						className="DesignCustomization__Label"
						ref={labelsEl}
					>
						{sortedItems.map((types, typeIndex) => (
							types.map((item, itemIndex) => (
								<span
									className="DesignCustomization__LabelTexts"
									data-custom-label-2d-index={`${typeIndex}-${itemIndex}`}
									key={`data-custom-label-index-${typeIndex}-${itemIndex}`}
									data-label={item.label}
								>
									{item.label}
								</span>
							))
						))}
					</div>

					<div className="DesignCustomization__PopupContainer">

						<div className="DesignCustomization__SelectContainer" data-cursor-hide>

							{sortedItems[0].length > 0 &&
								<div
									className="DesignCustomization__Type"
									data-accordeon-design-index={0}
									onKeyDown={handleOnAccordeonKeyDown}
									role="button"
									tabIndex={0}
									ref={ref => itemsAccordeonsEls.current[0] = ref}
								>
									<div
										className="DesignCustomization__TypeHeader"
										onClick={onAccordeonItemClick}
									>
										<h6 className="DesignCustomization__TypeName">Colors</h6>
										<div className="DesignCustomization__TypeIcon">
											<div className="DesignCustomization__TypeIconLines"></div>
											<div className="DesignCustomization__TypeIconLines"></div>
										</div>
									</div>
									<div className="DesignCustomization__TypeList">
										{sortedItems[0].map((item, itemIndex) => (
											<div
												className="DesignCustomization__TypeItemCheckbox"
												key={`design-customization-color-${itemIndex}`}
												style={{ backgroundImage: `url(${item.image1.fluid.src})` }}
												data-custom-type-index={0}
												data-custom-item-index={itemIndex}
												onClick={handleCheckboxClick}
												onKeyDown={handleOnCheckboxKeyDown}
												role="button"
												tabIndex={0}
											></div>
										))}
									</div>
								</div>
							}
							{sortedItems[1].length > 0 &&
								<div
									className="DesignCustomization__Type"
									data-accordeon-design-index={1}
									onKeyDown={handleOnAccordeonKeyDown}
									role="button"
									tabIndex={0}
									ref={ref => itemsAccordeonsEls.current[1] = ref}
								>
									<div
										className="DesignCustomization__TypeHeader"
										onClick={onAccordeonItemClick}
									>
										<h6 className="DesignCustomization__TypeName">Shapes</h6>
										<div className="DesignCustomization__TypeIcon">
											<div className="DesignCustomization__TypeIconLines"></div>
											<div className="DesignCustomization__TypeIconLines"></div>
										</div>
									</div>
									<div className="DesignCustomization__TypeList">
										<InputSelect
											placeholder="All"
											options={sortedItems[1]}
											defaultValue={sortedItems[1][0]}
											onChange={e => handleSelectChange(e, 'shapes')}
											className="DesignCustomization__TypeListSelect"
											optionsFontSize="0.9vw"
										/>
									</div>
								</div>
							}
							{sortedItems[2].length > 0 &&
								<div
									className="DesignCustomization__Type"
									data-accordeon-design-index={2}
									onKeyDown={handleOnAccordeonKeyDown}
									role="button"
									tabIndex={0}
									ref={ref => itemsAccordeonsEls.current[2] = ref}
								>
									<div
										className="DesignCustomization__TypeHeader"
										onClick={onAccordeonItemClick}
									>
										<h6 className="DesignCustomization__TypeName">Glazes - Standard</h6>
										<div className="DesignCustomization__TypeIcon">
											<div className="DesignCustomization__TypeIconLines"></div>
											<div className="DesignCustomization__TypeIconLines"></div>
										</div>
									</div>
									<div className="DesignCustomization__TypeList">
										{sortedItems[2].map((item, itemIndex) => (
											<div
												className="DesignCustomization__TypeItemCheckbox"
												key={`design-customization-glazes-standard-${itemIndex}`}
												style={{ background: `url(${item.image1.fluid.src})` }}
												data-custom-type-index={2}
												data-custom-item-index={itemIndex}
												onClick={handleCheckboxClick}
												onKeyDown={handleOnCheckboxKeyDown}
												role="button"
												tabIndex={0}
											></div>
										))}
									</div>
								</div>
							}
							{sortedItems[3].length > 0 &&
								<div
									className="DesignCustomization__Type"
									data-accordeon-design-index={3}
									onKeyDown={handleOnAccordeonKeyDown}
									role="button"
									tabIndex={0}
									ref={ref => itemsAccordeonsEls.current[3] = ref}
								>
									<div
										className="DesignCustomization__TypeHeader"
										onClick={onAccordeonItemClick}
									>
										<h6 className="DesignCustomization__TypeName">Glazes - Effects</h6>
										<div className="DesignCustomization__TypeIcon">
											<div className="DesignCustomization__TypeIconLines"></div>
											<div className="DesignCustomization__TypeIconLines"></div>
										</div>
									</div>
									<div className="DesignCustomization__TypeList">
										{sortedItems[3].map((item, itemIndex) => (
											<div
												className="DesignCustomization__TypeItemCheckbox"
												key={`design-customization-glazes-effects-${itemIndex}`}
												style={{ background: `url(${item.image1.fluid.src})` }}
												data-custom-type-index={3}
												data-custom-item-index={itemIndex}
												onClick={handleCheckboxClick}
												onKeyDown={handleOnCheckboxKeyDown}
												role="button"
												tabIndex={0}
											></div>
										))}
									</div>
								</div>
							}
							{sortedItems[4].length > 0 &&
								<div
									className="DesignCustomization__Type"
									data-accordeon-design-index={4}
									onKeyDown={handleOnAccordeonKeyDown}
									role="button"
									tabIndex={0}
									ref={ref => itemsAccordeonsEls.current[4] = ref}
								>
									<div
										className="DesignCustomization__TypeHeader"
										onClick={onAccordeonItemClick}
									>
										<h6 className="DesignCustomization__TypeName">Textures</h6>
										<div className="DesignCustomization__TypeIcon">
											<div className="DesignCustomization__TypeIconLines"></div>
											<div className="DesignCustomization__TypeIconLines"></div>
										</div>
									</div>
									<div className="DesignCustomization__TypeList">
										<InputSelect
											placeholder="All"
											options={sortedItems[4]}
											defaultValue={sortedItems[4][0]}
											onChange={e => handleSelectChange(e, 'shapes')}
											className="DesignCustomization__TypeListSelect"
											optionsFontSize="0.9vw"
										/>
									</div>
								</div>
							}
						</div>

						<div
							className={`DesignCustomization__ZoomWrapper ${isZoomedIn ? 'isZoomedIn' : ''}`}
							ref={zoomWrapperEl}
							onClick={handleZoomWrapperClick}
							onMouseMove={handleZoomWrapperMouseMove}
						>
							<svg
								className={`DesignCustomization__ImageCircleOverlay ${(currentIndex === 2 || currentIndex === 3 || (forceOverlayOnColorTypeImages && currentIndex === 0)) ? 'visible' : ''}`}
								viewBox="0 0 1080 800" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"><defs><path d="M0 0h1080v800H0V0zm540 675c151.878 0 275-123.122 275-275S691.878 125 540 125 265 248.122 265 400s123.122 275 275 275z" id="a"/></defs><use fill="#000" xlinkHref="#a" fillRule="evenodd"/>
							</svg>
							<div
								className="DesignCustomization__ZoomTranslate"
								ref={zoomTranslateEl}
							>
								<div
									className="DesignCustomization__ImageWrapper"
									ref={imageWrapperEl}
								>
									{sortedItems.map((types, typeIndex) => (
										types.map((item, itemIndex) => {
											return (
												<Picture
													className="DesignCustomization__Image"
													src={item.image1.fluid.src}
													srcSet={item.image1.fluid.srcSet}
													alt={item.image1.alt}
													sizes="150vw"
													data-custom-images-2d-index={`${typeIndex}-${itemIndex}`}
													key={`data-custom-image-index-${typeIndex}-${itemIndex}`}
												/>
											)
										})
									))}
								</div>
							</div>
						</div>

					</div>

				</div>
			}

		</div>
	)
}

export default DesignCustomization